import { DateTime } from 'luxon';
import { isAnnualSubscriptionPlan } from '../subscriptions/SubscriptionTier';
export const hasCustomDiscountOption = (options, key) => {
    var _a;
    if (!options || !key) {
        return false;
    }
    const customValue = (_a = options.customFilters) === null || _a === void 0 ? void 0 : _a[key];
    if (customValue === undefined) {
        return false;
    }
    return true;
};
export const getGiveawayCampaignId = (options) => {
    var _a;
    if (!hasCustomDiscountOption(options, 'giveawayCampaign')) {
        return undefined;
    }
    const giveaway = (_a = options.customFilters) === null || _a === void 0 ? void 0 : _a.giveawayCampaign;
    if (giveaway && typeof giveaway === 'object' && giveaway.campaignId) {
        return giveaway.campaignId;
    }
    return 'GiveawayCampaign';
};
const getSunsetPlusTierTag = (options) => {
    var _a;
    const tierSetting = (_a = options === null || options === void 0 ? void 0 : options.customFilters) === null || _a === void 0 ? void 0 : _a.sunsetPlusTier;
    if (!tierSetting) {
        return undefined;
    }
    if (typeof tierSetting !== 'object') {
        return undefined;
    }
    const tierToTag = {
        basic: 'SunsetPlusBasic',
        premium: 'SunsetPlusPremium',
        exclusive: 'SunsetPlusExclusive',
        business: 'SunsetPlusBusiness',
    };
    const baseTag = tierToTag[tierSetting.tier] ||
        'SunsetPlus' + (tierSetting.tier.charAt(0).toUpperCase() + tierSetting.tier.slice(1));
    if (!baseTag) {
        return undefined;
    }
    return tierSetting.annual ? `${baseTag}Annual` : baseTag;
};
export const isPriceRuleValid = (membership, rule, subscriptionCreatedDate, subscriptionSwitchedDate) => {
    var _a;
    const untilDate = new Date(rule.options.validUntil);
    const checkDate = membership.lastIndexOf('%') !== -1
        ? untilDate.setDate(untilDate.getDate() - 1)
        : subscriptionSwitchedDate || subscriptionCreatedDate;
    if (membership && rule.options) {
        const normalizedMembership = stripVersion(membership);
        const validFrom = !rule.options.validFrom || checkDate > new Date(rule.options.validFrom);
        const validUntil = !rule.options.validUntil || checkDate < new Date(rule.options.validUntil);
        if (validFrom && validUntil) {
            const sunsetPlusTier = (_a = rule.options.customFilters) === null || _a === void 0 ? void 0 : _a.sunsetPlusTier;
            if (typeof (sunsetPlusTier === null || sunsetPlusTier === void 0 ? void 0 : sunsetPlusTier.annual) === 'boolean' &&
                sunsetPlusTier.annual !== isAnnualSubscriptionPlan(normalizedMembership)) {
                return false;
            }
            const normalizedLower = normalizedMembership.toLowerCase();
            if ((sunsetPlusTier === null || sunsetPlusTier === void 0 ? void 0 : sunsetPlusTier.tier) && normalizedLower.includes(sunsetPlusTier.tier.toLowerCase())) {
                return true;
            }
            if (normalizedLower.includes('employee') && hasCustomDiscountOption(rule.options, 'employeeDiscount')) {
                return true;
            }
            if (normalizedLower.includes('sunsetpoints') && hasCustomDiscountOption(rule.options, 'sunsetPoints')) {
                return true;
            }
            return false;
        }
    }
    return false;
};
export const isGlobalCartRule = (rule) => {
    return hasCustomDiscountOption(rule.options, 'cartDiscount');
};
export const shouldApplyEmployeeDiscount = (rule, options) => {
    return (hasCustomDiscountOption(rule.options, 'employeeDiscount') &&
        options.employeeDiscountToken &&
        options.employeeDiscountToken.token &&
        new Date(options.employeeDiscountToken.expiresAt) > new Date());
};
export const shouldApplySunsetPointsDiscount = (rule, options) => {
    return (hasCustomDiscountOption(rule.options, 'sunsetPoints') &&
        options.sunsetPoints &&
        options.sunsetPoints.totalPoints > 0);
};
export const stripVersion = function (str) {
    if (str.indexOf(' %') !== -1) {
        const adjustedStr = str.replace(' %', '%');
        return adjustedStr.lastIndexOf(' ') !== -1 ? str.substr(0, adjustedStr.lastIndexOf(' ')) : str;
    }
    return str.lastIndexOf(' ') !== -1 && str.lastIndexOf('%') !== -1 ? str.substr(0, str.lastIndexOf(' ')) : str;
};
export const getShopPattern = (shopPattern) => {
    return !shopPattern || Array.isArray(shopPattern) ? shopPattern : shopPattern.list;
};
export const getIsWhitelist = (shopPattern) => {
    return !shopPattern || Array.isArray(shopPattern) ? true : shopPattern.isWhitelist;
};
export const shouldRuleBeAppliedToShop = (shopPattern, shopId) => {
    const shopsList = getShopPattern(shopPattern);
    const isWhitelist = getIsWhitelist(shopPattern);
    if (shopPattern && typeof shopPattern === 'object' && !Array.isArray(shopPattern)) {
        if (Array.isArray(shopsList) && shopsList.length > 0) {
            return isWhitelist
                ? shopsList.find((s) => s && s.indexOf(shopId) !== -1)
                : !shopsList.find((s) => s && s.indexOf(shopId) !== -1);
        }
        return !isWhitelist;
    }
    return (!shopsList ||
        (Array.isArray(shopsList) && (shopsList.includes(shopId) || shopsList.includes('*') || shopsList.includes('*-*-*'))));
};
export const getProductSpecificDiscount = ({ price, discount }) => {
    const discountValue = Number(discount.discountValue);
    if (discount.discountType === 'percentDiscount') {
        return (discountValue * price) / 100;
    }
    if (discount.discountType === 'simpleDiscountFixedAmount') {
        return discountValue;
    }
    return 0;
};
export const isProductSpecificDiscount = (discount) => discount &&
    (!discount.voucherPattern || !discount.voucherPattern.length) &&
    ['simpleDiscountFixedAmount', 'percentDiscount'].includes(discount.discountType);
export const isFreeMealPriceRule = (rule) => {
    return hasCustomDiscountOption(rule.options, 'freeMeal');
};
export const isSunsetPointsPriceRule = (rule) => {
    return hasCustomDiscountOption(rule === null || rule === void 0 ? void 0 : rule.options, 'sunsetPoints');
};
export const isGiveawayCampaignDiscountOption = (options) => {
    return hasCustomDiscountOption(options, 'giveawayCampaign');
};
export const getCustomDiscountTags = (options) => {
    const tags = [];
    const sunsetPlusTag = getSunsetPlusTierTag(options);
    if (sunsetPlusTag) {
        tags.push(sunsetPlusTag);
    }
    if (hasCustomDiscountOption(options, 'cartDiscount')) {
        tags.push('CartDiscount');
    }
    if (hasCustomDiscountOption(options, 'employeeDiscount')) {
        tags.push('SunsetPlusForEmployees');
    }
    if (hasCustomDiscountOption(options, 'sunsetPoints')) {
        tags.push('SunsetPoints');
    }
    if (hasCustomDiscountOption(options, 'freeMeal')) {
        tags.push('FreeMeal');
    }
    if (hasCustomDiscountOption(options, 'giveawayCampaign')) {
        tags.push('GiveawayCampaign');
    }
    return tags;
};
export const shouldApplyCustomPriceRule = (rule, options) => {
    if (shouldApplySunsetPointsDiscount(rule, options)) {
        return true;
    }
    if (shouldApplyEmployeeDiscount(rule, options)) {
        return true;
    }
    if (isFreeMealPriceRule(rule) && !rule.productSpecific) {
        return true;
    }
    if (isGlobalCartRule(rule)) {
        return true;
    }
    return false;
};
export const isRuleActive = (rule, pickupTimeZone = null) => {
    if (!Array.isArray(rule.timePattern) || rule.timePattern.length === 0) {
        return true;
    }
    const nowLuxon = DateTime.now().setZone(pickupTimeZone);
    const currentWeekDay = nowLuxon.weekday;
    if (!Array.isArray(rule.weekDay) || !rule.weekDay.includes(currentWeekDay)) {
        return false;
    }
    const nowSeconds = nowLuxon.hour * 3600 + nowLuxon.minute * 60 + nowLuxon.second;
    const inRange = rule.timePattern.some(({ startTime, endTime }) => {
        const [startHour, startMinute = 0, startSecond = 0] = startTime.split(':').map(Number);
        const [endHour, endMinute = 0, endSecond = 0] = endTime.split(':').map(Number);
        const startSeconds = startHour * 3600 + startMinute * 60 + startSecond;
        const endSeconds = endHour * 3600 + endMinute * 60 + endSecond;
        return nowSeconds >= startSeconds && nowSeconds <= endSeconds;
    });
    return inRange;
};
export const isRuleWithTimePatternActive = (timePattern, pickupTimeZone = null) => {
    if ((timePattern === null || timePattern === void 0 ? void 0 : timePattern.length) === 0) {
        return true;
    }
    if (!Array.isArray(timePattern)) {
        return false;
    }
    const nowLuxon = DateTime.now().setZone(pickupTimeZone);
    const nowSeconds = nowLuxon.hour * 3600 + nowLuxon.minute * 60 + nowLuxon.second;
    const inRange = timePattern.some(({ startTime, endTime }) => {
        if (!startTime || !endTime)
            return false;
        const [startHour, startMinute = 0, startSecond = 0] = startTime.split(':').map(Number);
        const [endHour, endMinute = 0, endSecond = 0] = endTime.split(':').map(Number);
        const startSeconds = startHour * 3600 + startMinute * 60 + startSecond;
        const endSeconds = endHour * 3600 + endMinute * 60 + endSecond;
        if (endSeconds >= startSeconds) {
            return nowSeconds >= startSeconds && nowSeconds <= endSeconds;
        }
        return nowSeconds >= startSeconds || nowSeconds <= endSeconds;
    });
    return inRange;
};
